import React, { useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Api from 'utils/api';

import { TRANSLATIONS as t } from 'constants/translations';
import styles from './PaymentPage.module.css';

// ─────────────────────────────────────────────
// 키움페이 설정 (환경에 맞게 수정)
// ─────────────────────────────────────────────
const KIWOOM_CONFIG = {
    CPID: process.env.REACT_APP_KIWOOM_CPID,
    HASH_ENDPOINT: `${process.env.REACT_APP_API_BASE_URL}/api/kiwoom/getKiwoomENC`,
    PAY_URL: process.env.REACT_APP_KIWOOM_PAY_URL,
};

// 결제수단별 PAYMETHOD 코드
const PAYMETHOD_MAP = {
    card: 'CARD',
    naverpay: 'NAVERPAY',
    kakaopay: 'KAKAOPAY',
    samsungpay: 'SAMSUNGPAY',
};

// 간편결제 세부 수단 목록
const EASY_PAY_OPTIONS = [
    {
        key: 'naverpay',
        label: '네이버페이',
        imgSrc: '/payment_icon_naver.svg',
        imgAlt: 'naverpay',
        imgClass: styles.iconNaver,
    },
    {
        key: 'kakaopay',
        label: '카카오페이',
        imgSrc: '/payment_icon_kakao.png',
        imgAlt: 'kakaopay',
        imgClass: styles.iconKakao,
    },
    {
        key: 'samsungpay',
        label: '삼성페이',
        imgSrc: '/payment_icon_samsungpay.png',
        imgAlt: 'samsungpay',
        imgClass: styles.iconSamsungPay,
    },
];

// 결제 타입 감지
const detectType = () => {
    const ua = navigator.userAgent;
    if (/Android|iPhone|iPad|iPod|Mobile/i.test(ua)) return 'M';
    return 'P';
};

// 타임스탬프 기반 주문번호 생성
const generateOrderNo = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
};

// cart 아이템에서 표시용 상품명 추출
const getItemName = (item, lang) => {
    return (
        item.productNames?.[lang] ||
        item.productNames?.Ko ||
        item.name ||
        ''
    );
};

// ─────────────────────────────────────────────
// PaymentPage Component
// ─────────────────────────────────────────────
const PaymentPage = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const [lang] = useState(() => {
        const savedLang = localStorage.getItem('vending_lang');
        return savedLang || location.state?.lang || 'Ko';
    });

    const { cart, totalPrice } = location.state || { cart: [], totalPrice: 0 };

    // 'card' | 'easy' — 상위 그룹 선택
    const [paymentGroup, setPaymentGroup] = useState('card');
    // 간편결제 세부 수단 선택 ('naverpay' | 'kakaopay' | 'samsungpay' | null)
    const [easyPayMethod, setEasyPayMethod] = useState(null);

    const [isLoading, setIsLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    const formRef = useRef(null);

    // ── 상위 그룹 클릭 핸들러 ──
    const handleGroupSelect = (group) => {
        setPaymentGroup(group);
        setErrorMsg('');
        if (group === 'card') setEasyPayMethod(null);
    };

    // ── 간편결제 세부 수단 클릭 핸들러 ──
    const handleEasyPaySelect = (key) => {
        setEasyPayMethod(key);
        setErrorMsg('');
    };

    // 결제 데이터가 없는 경우 처리
    if (!cart || cart.length === 0) {
        return (
            <div className={styles.errorContainer}>
                <p>{t.noInfo?.[lang] || '결제 정보가 없습니다.'}</p>
                <button onClick={() => navigate(-1)}>{t.confirm?.[lang] || '확인'}</button>
            </div>
        );
    }

    // ─── 1단계: KIWOOM_ENC 취득 ───
    const fetchKiwoomENC = async ({ CPID, PAYMETHOD, ORDERNO, TYPE, AMOUNT }) => {
        const response = await fetch(KIWOOM_CONFIG.HASH_ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({ CPID, PAYMETHOD, ORDERNO, TYPE, AMOUNT }),
        });
        if (!response.ok) throw new Error(`서버 오류: ${response.status}`);
        const data = await response.json();
        console.log('[키움페이] [1단계] 백엔드 응답:', data);
        if (!data.KIWOOM_ENC) throw new Error('KIWOOM_ENC 값을 받지 못했습니다.');
        return data.KIWOOM_ENC;
    };

    // ─── 2단계: 키움페이 결제창 호출 ───
    const submitToKiwoomPay = ({ KIWOOM_ENC, PAYMETHOD, ORDERNO, TYPE, AMOUNT, PRODUCTNAME, PRODUCTCODE }) => {
        const form = formRef.current;
        const payUrl = KIWOOM_CONFIG.PAY_URL;

        const setField = (name, value) => {
            let el = form.querySelector(`[name="${name}"]`);
            if (!el) {
                el = document.createElement('input');
                el.type = 'hidden';
                el.name = name;
                form.appendChild(el);
            }
            el.value = value ?? '';
        };

        setField('PAYMETHOD', PAYMETHOD);
        setField('TYPE', TYPE);
        setField('CPID', KIWOOM_CONFIG.CPID);
        setField('ORDERNO', ORDERNO);
        setField('PRODUCTTYPE', '1');
        setField('AMOUNT', AMOUNT);
        setField('PRODUCTNAME', PRODUCTNAME);
        setField('PRODUCTCODE', PRODUCTCODE);
        setField('USERID', localStorage.getItem('vending_userid') || 'guest');

        setField('KIWOOM_ENC', KIWOOM_ENC);
        

        setField('TAXFREECD', '00');
        
        if (TYPE === 'M' || TYPE === 'P') {
            setField('HOMEURL', `${window.location.origin}/success`);
            setField('CLOSEURL', `${window.location.origin}/payment`);
            setField('FAILURL', `${window.location.origin}/payment`);
        }

        if (TYPE === 'P') {
            window.open('', 'KIWOOMPAY', 'width=468,height=750,scrollbars=yes');
            form.target = 'KIWOOMPAY';
        } else {
            form.target = '_self';
        }

        form.action = payUrl;
        form.method = 'post';
        form.acceptCharset = 'euc-kr';

        const formData = {};
        Array.from(form.elements).forEach(el => { if (el.name) formData[el.name] = el.value; });
        console.log('[키움페이] [2단계] 결제창 호출 파라미터:', { action: payUrl, ...formData });

        form.submit();
    };

    // ─── 결제 실행 ───
    const handleProcessPayment = async () => {
        if (paymentGroup === 'easy' && !easyPayMethod) {
            setErrorMsg('간편결제 수단을 선택해주세요.');
            return;
        }

        setErrorMsg('');
        setIsLoading(true);

        try {
            // TODO: 키움페이 연동 임시 비활성화 — 테스트 결제 API 사용
            const serialNumber = localStorage.getItem('vending_serial');
            const paymentDetailReqList = cart.map(item => ({
                machineColumnId: item.id,
                purchaseCount: item.count,
            }));
            const response = await Api.post('/sale/payment/test', { serialNumber, paymentDetailReqList });
            if (!response) return;
            localStorage.setItem('vending_last_order', JSON.stringify({ cart, totalPrice }));
            navigate('/success');

            // const methodKey = paymentGroup === 'card' ? 'card' : easyPayMethod;
            // const PAYMETHOD = PAYMETHOD_MAP[methodKey];
            // const ORDERNO = generateOrderNo();
            // const TYPE = detectType();
            // const AMOUNT = String(totalPrice);
            // const firstName = getItemName(cart[0], lang);
            // const PRODUCTNAME = cart.length > 1
            //     ? `${firstName} 외 ${cart.length - 1}개`
            //     : firstName;
            // const PRODUCTCODE = cart[0]?.id ? String(cart[0].id) : '00001';

            // const KIWOOM_ENC = await fetchKiwoomENC({
            //     CPID: KIWOOM_CONFIG.CPID,
            //     PAYMETHOD,
            //     ORDERNO,
            //     TYPE,
            //     AMOUNT,
            // });

            // submitToKiwoomPay({ KIWOOM_ENC, PAYMETHOD, ORDERNO, TYPE, AMOUNT, PRODUCTNAME, PRODUCTCODE });

        } catch (err) {
            console.error('결제 오류:', err);
            setErrorMsg(err.message || '결제 처리 중 오류가 발생했습니다. 다시 시도해주세요.');
        } finally {
            setIsLoading(false);
        }
    };

    const selectedEasyLabel = EASY_PAY_OPTIONS.find((o) => o.key === easyPayMethod)?.label;

    return (
        <div className={styles.container}>
            {/* 숨겨진 키움페이 POST 폼 */}
            <form ref={formRef} style={{ display: 'none' }} />

            <header className={styles.header}>
                <button className={styles.backBtn} onClick={() => navigate(-1)}>〈</button>
                <h2 className={styles.title}>{t.paymentTitle?.[lang] || '결제'}</h2>
            </header>

            <main className={styles.content}>
                {/* 주문 상품 리스트 */}
                <section className={styles.orderSection}>
                    <h3 className={styles.sectionTitle}>{t.orderList?.[lang] || '주문 상품'}</h3>
                    <div className={styles.orderList}>
                        {cart.map((item) => {
                            const itemName = getItemName(item, lang);
                            return (
                                <div key={item.id} className={styles.orderItem}>
                                    <div className={styles.itemTextInfo}>
                                        <p className={styles.itemName}>{itemName}</p>
                                        <p className={styles.itemSubText}>
                                            {t.quantity?.[lang] || '수량'} {item.count}{t.unit?.[lang] || '개'}
                                        </p>
                                    </div>
                                    <div className={styles.itemPrice}>
                                        {(item.price * item.count).toLocaleString()}{t.currency?.[lang] || '원'}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>

                <div className={styles.amountSummary}>
                    <span className={styles.amountLabel}>{t.amountLabel?.[lang] || '결제 금액'}</span>
                    <span className={styles.amountValue}>
                        {totalPrice.toLocaleString()}{t.currency?.[lang] || '원'}
                    </span>
                </div>

                <div className={styles.whiteSpace} />

                {/* ── 결제 수단 선택 ── */}
                <section className={styles.paymentSection}>
                    <h3 className={styles.sectionTitle}>{t.paymentMethod?.[lang] || '결제 수단'}</h3>

                    {/* ① 신용/체크카드 */}
                    <div
                        className={`${styles.methodGroup} ${paymentGroup === 'card' ? styles.methodGroupSelected : ''}`}
                        onClick={() => handleGroupSelect('card')}
                    >
                        <div className={styles.radioRow}>
                            <div className={`${styles.radioButton} ${paymentGroup === 'card' ? styles.checked : ''}`} />
                            <span className={styles.methodLabel}>
                                💳 {t.creditCard?.[lang] || '신용/체크카드'}
                            </span>
                        </div>
                        {paymentGroup === 'card' && (
                            <div className={styles.cardSelectorBox}>
                                <span>💳 {t.creditCard?.[lang] || '신용/체크카드'}</span>
                            </div>
                        )}
                    </div>

                    <div className={styles.divider} />

                    {/* ② 간편결제 묶음 */}
                    <div
                        className={`${styles.methodGroup} ${paymentGroup === 'easy' ? styles.methodGroupSelected : ''}`}
                        onClick={() => handleGroupSelect('easy')}
                    >
                        {/* 간편결제 헤더 행 */}
                        <div className={styles.radioRow}>
                            <div className={`${styles.radioButton} ${paymentGroup === 'easy' ? styles.checked : ''}`} />
                            <span className={styles.methodLabel}>
                                {t.easyPay?.[lang] || '간편결제'}
                            </span>
                            {/* 세부 수단이 선택된 경우 선택 라벨 표시, 아니면 아이콘 미리보기 */}
                            {paymentGroup === 'easy' && easyPayMethod ? (
                                <span className={styles.easySelectedLabel}>{selectedEasyLabel}</span>
                            ) : (
                                <div className={styles.easyIcons}>
                                    <img src="/payment_icon_naver.svg" alt="naverpay" className={styles.iconNaver} />
                                    <img src="/payment_icon_kakao.png" alt="kakaopay" className={styles.iconKakao} />
                                    <img src="/payment_icon_samsungpay.png" alt="samsungpay" className={styles.iconSamsungPay} />
                                </div>
                            )}
                        </div>

                        {/* 세부 수단 버튼들 — 'easy' 선택 시 펼침 */}
                        {paymentGroup === 'easy' && (
                            <div className={styles.easySubOptions}>
                                {EASY_PAY_OPTIONS.map((opt) => (
                                    <button
                                        key={opt.key}
                                        type="button"
                                        className={`${styles.easySubBtn} ${easyPayMethod === opt.key ? styles.easySubBtnSelected : ''}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleEasyPaySelect(opt.key);
                                        }}
                                    >
                                        <img src={opt.imgSrc} alt={opt.imgAlt} className={opt.imgClass} />
                                        <span className={styles.easySubLabel}>{opt.label}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </section>

                {/* 에러 메시지 */}
                {errorMsg && (
                    <div className={styles.errorMsg}>⚠️ {errorMsg}</div>
                )}
            </main>

            <footer className={styles.footer}>
                <button
                    className={`${styles.payBtn} ${isLoading ? styles.payBtnLoading : ''}`}
                    onClick={handleProcessPayment}
                    disabled={isLoading}
                >
                    {isLoading
                        ? (t.processing?.[lang] || '처리 중...')
                        : paymentGroup === 'easy' && selectedEasyLabel
                            ? `${totalPrice.toLocaleString()}${t.currency?.[lang] || '원'} ${selectedEasyLabel}로 결제하기`
                            : `${totalPrice.toLocaleString()}${t.currency?.[lang] || '원'} ${t.payBtn?.[lang] || '결제하기'}`
                    }
                </button>
            </footer>
        </div>
    );
};

export default PaymentPage;